import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Shield,
  ArrowRight,
  FileText,
  AlertTriangle,
  Server,
  Heart,
  Landmark,
  ShoppingCart,
  Satellite,
  Building,
  Check,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const SOCServicePage = () => {
  useEffect(() => {
    document.title = '24/7 SOC as a Service | Atlas Defenders';
  }, []);




  const [currentIndustrySlide, setCurrentIndustrySlide] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);




  // Company website URLs for redirects






  // Statistics


  // Customer testimonials


  // Industries carousel data - Industries We Protect
  const industriesCarousel = [
    {
      id: 'banking-finance',
      title: 'Banking & Finance',
      description: 'PCI-DSS compliance, fraud detection, and financial data protection for banks, credit unions, and fintech companies.',
      image: 'https://images.pexels.com/photos/259200/pexels-photo-259200.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1'
    },
    {
      id: 'healthcare',
      title: 'Healthcare',
      description: 'HIPAA compliance, patient data protection, and medical device security for hospitals, clinics, and healthcare providers.',
      image: 'https://images.pexels.com/photos/236380/pexels-photo-236380.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1'
    },
    {
      id: 'ecommerce-cloud',
      title: 'E-Commerce & Cloud Providers',
      description: 'Customer data protection, payment security, and cloud infrastructure security for online businesses.',
      image: 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1'
    },
    {
      id: 'government-public',
      title: 'Government & Public Sector',
      description: 'FISMA compliance, classified data protection, and cybersecurity frameworks for federal, state, and local agencies.',
      image: 'https://images.pexels.com/photos/8112199/pexels-photo-8112199.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1'
    },
    {
      id: 'critical-infrastructure',
      title: 'Critical Infrastructure',
      description: 'Industrial control systems security, SCADA protection, and operational technology security for utilities and energy.',
      image: 'https://images.pexels.com/photos/2800832/pexels-photo-2800832.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1'
    },
    {
      id: 'telecom',
      title: 'Telecom',
      description: 'Network security, subscriber data protection, and telecommunications infrastructure security for service providers.',
      image: 'https://images.pexels.com/photos/442150/pexels-photo-442150.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&dpr=1'
    }
  ];

  // Enhanced Carousel navigation functions with auto-animation
  const nextSlide = () => {
    setCurrentIndustrySlide((prev) => (prev + 1) % Math.max(1, industriesCarousel.length - 1));
  };

  const prevSlide = () => {
    setCurrentIndustrySlide((prev) => (prev - 1 + Math.max(1, industriesCarousel.length - 1)) % Math.max(1, industriesCarousel.length - 1));
  };

  // Auto-animation effect for carousel
  useEffect(() => {
    if (!isCarouselPaused) {
      const interval = setInterval(() => {
        nextSlide();
      }, 4000); // Auto-advance every 4 seconds

      return () => clearInterval(interval);
    }
  }, [isCarouselPaused, currentIndustrySlide]);

  // Load Spline viewer script
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = 'https://unpkg.com/@splinetool/viewer@1.10.33/build/spline-viewer.js';
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      {/* Hero Section - Sophos MDR Style */}
      <section className="relative min-h-[90vh] bg-[#001F5F] overflow-hidden flex flex-col justify-center">
        {/* Background Gradients */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#001744] via-[#003399] to-[#001F5F]"></div>

        {/* Abstract Blue Waves/Curves Background Effect */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute bottom-0 right-0 w-full h-[800px] bg-gradient-radial from-blue-400/20 to-transparent transform translate-y-1/2 translate-x-1/4"></div>
          <svg className="absolute bottom-0 left-0 w-full h-full" viewBox="0 0 1440 800" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M-100 800 C 200 600, 400 700, 600 500 S 1000 400, 1500 600" stroke="url(#gradient-line)" strokeWidth="2" strokeOpacity="0.3" fill="none" />
            <path d="M-100 850 C 250 650, 450 750, 650 550 S 1050 450, 1550 650" stroke="url(#gradient-line)" strokeWidth="2" strokeOpacity="0.2" fill="none" />
            <defs>
              <linearGradient id="gradient-line" x1="0" y1="0" x2="1" y2="0">
                <stop stopColor="#3B82F6" stopOpacity="0" />
                <stop offset="0.5" stopColor="#3B82F6" />
                <stop offset="1" stopColor="#3B82F6" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="container mx-auto px-4 relative z-10 pt-20 pb-40">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">

            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-medium text-white mb-6 leading-[1.1] tracking-tight">
                Managed Detection and Response (MDR) services
              </h1>
              <p className="text-xl text-blue-100 mb-10 max-w-lg font-light">
                MDR that meets you where you are in your security journey. Complete visibility, 24/7 monitoring, and expert response.
              </p>

              <div className="flex flex-wrap items-center gap-6">
                <Link
                  to="/contact"
                  className="px-8 py-3.5 rounded-full border border-blue-400 text-white font-semibold hover:bg-blue-600 hover:border-blue-600 transition-all duration-300"
                >
                  Speak with an expert
                </Link>
                <Link
                  to="/#services"
                  className="text-white hover:text-blue-300 font-medium flex items-center transition-colors"
                >
                  Discover All Atlas Defenders Services <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </motion.div>

            {/* Right Content - Generated SOC Team Image with Logo Overlay */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative flex justify-center items-center h-full min-h-[400px]">
                {/* Glow Effect behind Logo */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-500/20 blur-3xl rounded-full"></div>

                {/* Main Logo */}
                <img
                  src="/Logos/atlasdefenders_white_logo.svg"
                  alt="Atlas Defenders"
                  className="w-full max-w-xl h-auto object-contain drop-shadow-2xl relative z-10 transform hover:scale-105 transition-transform duration-700"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Spacer was also removed since stats cards are gone */}




      {/* SOC Definition Section - Zabbix Style Redesign */}
      <section className="bg-[#001F5F] relative overflow-hidden flex items-center py-24 lg:py-0 lg:h-[700px]">
        {/* Background Gradients */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-blue-900/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-blue-900/10 blur-3xl rounded-full"></div>
        </div>

        <div className="w-full relative z-10 h-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-full items-center">

            {/* Left Content - IMAGE (Order 1) */}
            <div className="relative h-full hidden lg:flex items-center justify-center order-1">
              <div className="relative w-full h-full overflow-hidden">
                <img
                  src="/images/Soc-as-a-service2.png"
                  alt="SOC as a Service Professional"
                  className="w-full h-full object-cover object-center absolute inset-0"
                />
                {/* Gradient overlay blending to the right */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#001F5F]/30 to-[#001F5F] z-10"></div>
              </div>
            </div>

            {/* Right Content - TEXT (Order 2) */}
            <div className="flex flex-col justify-center px-4 lg:pl-16 lg:pr-24 z-20 order-2">
              <div className="mb-4">
                <span className="text-blue-400 font-bold tracking-widest uppercase text-sm">Managed Detection & Response</span>
              </div>

              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                SOC AS A<br />
                <span className="text-white">SERVICE.</span>
              </h2>

              <p className="text-blue-100/90 text-lg leading-relaxed mb-10 max-w-xl">
                SOC as a Service (SOCaaS) is a fully managed cybersecurity solution delivered remotely by Atlas Defenders to detect, analyze, and respond to threats across your environment. Our SOC is staffed by certified professionals and powered by next-gen tools.
              </p>

              <div>
                <Link
                  to="/contact"
                  className="inline-block bg-white hover:bg-gray-100 text-[#001F5F] font-bold py-4 px-8 rounded transition-colors duration-300 uppercase tracking-wide text-sm shadow-lg"
                >
                  Get Protected
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>



      {/* Strategic Benefits We Offer Section - Modern Card Grid Style */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">

          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Strategic <span className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-900 bg-clip-text text-transparent">Benefits</span> We Offer
            </h2>
            <p className="text-xl text-slate-500 max-w-3xl mx-auto font-light leading-relaxed">
              Comprehensive cybersecurity solutions designed to protect, detect, and respond to threats with enterprise-grade precision.
            </p>
          </motion.div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">

            {/* Card 1: Detection */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="group relative"
            >
              {/* Card Content with Gradient Hover Background */}
              <div className="relative h-full bg-gradient-to-b from-slate-900 to-slate-900 group-hover:from-slate-900 group-hover:to-blue-700 rounded-xl p-8 flex flex-col items-start border border-slate-700 group-hover:border-blue-500 transition-all duration-500 shadow-xl overflow-hidden">
                {/* Icon */}
                {/* Icon - Simplified & Light */}
                <div className="mb-6 relative z-10 p-3 bg-white/5 rounded-2xl w-fit border border-white/10 group-hover:bg-blue-600/20 group-hover:border-blue-400/50 transition-colors duration-300">
                  <Shield size={32} className="text-white/80 group-hover:text-blue-100 transition-colors" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Detection</h3>
                <p className="text-slate-300 text-base mb-6 leading-relaxed relative z-10">
                  Identify threats before they strike. Our advanced detection capabilities leverage AI and expert analysis to uncover hidden risks.
                </p>

                {/* List - Compact */}
                <ul className="space-y-3 mb-8 flex-grow relative z-10">
                  {[
                    "24/7 real-time monitoring",
                    "AI & behavioral analytics",
                    "Log correlation (Endpoint/Net/Cloud)",
                    "Early malware & intrusion detection",
                    "Integrated threat intelligence"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <div className="mt-1 mr-3 flex-shrink-0">
                        <div className="w-4 h-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Check size={10} className="text-blue-400" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-300 text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Card 2: Response */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div className="relative h-full bg-gradient-to-b from-slate-900 to-slate-900 group-hover:from-slate-900 group-hover:to-blue-700 rounded-xl p-8 flex flex-col items-start border border-slate-700 group-hover:border-blue-500 transition-all duration-500 shadow-xl overflow-hidden">
                <div className="mb-6 relative z-10 p-3 bg-white/5 rounded-2xl w-fit border border-white/10 group-hover:bg-blue-600/20 group-hover:border-blue-400/50 transition-colors duration-300">
                  <AlertTriangle size={32} className="text-white/80 group-hover:text-blue-100 transition-colors" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Response</h3>
                <p className="text-slate-300 text-base mb-6 leading-relaxed relative z-10">
                  Immediate action. Controlled impact. We don't just alert you; we act to contain and neutralize threats instantly.
                </p>

                <ul className="space-y-3 mb-8 flex-grow relative z-10">
                  {[
                    "Instant incident containment",
                    "Automated SOAR playbooks",
                    "Expert-led investigation",
                    "Minimized business downtime",
                    "Real-time team collaboration"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <div className="mt-1 mr-3 flex-shrink-0">
                        <div className="w-4 h-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Check size={10} className="text-blue-400" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-200 text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Card 3: Reporting */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="group relative"
            >
              <div className="relative h-full bg-gradient-to-b from-slate-900 to-slate-900 group-hover:from-slate-900 group-hover:to-blue-700 rounded-xl p-8 flex flex-col items-start border border-slate-700 group-hover:border-blue-500 transition-all duration-500 shadow-xl overflow-hidden">
                <div className="mb-6 relative z-10 p-3 bg-white/5 rounded-2xl w-fit border border-white/10 group-hover:bg-blue-600/20 group-hover:border-blue-400/50 transition-colors duration-300">
                  <FileText size={32} className="text-white/80 group-hover:text-blue-100 transition-colors" />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">Reporting & Compliance</h3>
                <p className="text-slate-300 text-base mb-6 leading-relaxed relative z-10">
                  Clear insights. Audit-ready results. Get detailed reports that simplify compliance and executive updates.
                </p>

                <ul className="space-y-3 mb-8 flex-grow relative z-10">
                  {[
                    "Real-time executive dashboards",
                    "Detailed incident reports",
                    "Weekly/Monthly performance metrics",
                    "Compliance support (ISO, GDPR)",
                    "Audit-ready log retention"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <div className="mt-1 mr-3 flex-shrink-0">
                        <div className="w-4 h-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Check size={10} className="text-blue-400" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-200 text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

          </div>
        </div >
      </section >





      {/* Our Core SOC Service Section - Sophos AI Portfolio Style (Zig-Zag) */}
      <section className="py-24 bg-gradient-to-b from-blue-50/50 to-white relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">

          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-24"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-slate-900">
              CORE <span className="bg-gradient-to-r from-blue-700 via-blue-800 to-blue-900 bg-clip-text text-transparent">SOC SERVICES</span>
            </h2>
            <p className="text-xl text-slate-500 max-w-4xl mx-auto leading-relaxed font-light">
              Comprehensive security operations delivered through industry-leading tools and expert analysis
            </p>
          </motion.div>

          <div className="space-y-32">

            {/* 1. Managed XDR - Left Visual, Right Text */}
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1 relative"
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-500/20 blur-3xl rounded-full"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                  <img src="/images/crowdstike_xdr.webp" alt="CrowdStrike XDR Dashboard" className="w-full h-auto object-contain" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1"
              >
                <h3 className="text-3xl font-bold text-slate-900 mb-2">Managed XDR</h3>
                <p className="text-lg text-slate-500 mb-6 font-medium">Extended Detection & Response</p>
                <ul className="space-y-5">
                  {[
                    "Real-time detection across endpoints, networks, servers & cloud",
                    "Correlation of security events to reduce alert fatigue",
                    "AI-powered threat prioritization & automation",
                    "Integration with SIEM & SOAR for faster response",
                    "Complete visibility across your digital assets"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start group">
                      <div className="mt-1 mr-4 flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 shadow-md shadow-blue-500/20 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 ring-1 ring-white/50">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-700 font-medium leading-relaxed group-hover:text-blue-900 transition-colors duration-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* 2. EDR - Right Visual, Left Text */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1 relative"
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-cyan-400/20 blur-3xl rounded-full"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                  <img src="/Logos/edr_image.png" alt="EDR Dashboard" className="w-full h-auto object-contain" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1"
              >
                <h3 className="text-3xl font-bold text-slate-900 mb-2">EDR</h3>
                <p className="text-lg text-slate-500 mb-6 font-medium">Endpoint Detection & Response</p>
                <ul className="space-y-5">
                  {[
                    "Continuous monitoring of endpoint activity",
                    "Rapid isolation of compromised machines",
                    "Behavioral analysis to detect advanced threats",
                    "Malware, ransomware, and lateral movement detection",
                    "Centralized threat intelligence enrichment"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start group">
                      <div className="mt-1 mr-4 flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 shadow-md shadow-blue-500/20 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 ring-1 ring-white/50">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-700 font-medium leading-relaxed group-hover:text-blue-900 transition-colors duration-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* 3. SIEM - Left Visual, Right Text */}
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1 relative"
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-indigo-400/20 blur-3xl rounded-full"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                  <img src="/Logos/siem_image.png" alt="SIEM Dashboard" className="w-full h-auto object-contain" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1"
              >
                <h3 className="text-3xl font-bold text-slate-900 mb-2">SIEM</h3>
                <p className="text-lg text-slate-500 mb-6 font-medium">Security Information & Event Management</p>
                <ul className="space-y-5">
                  {[
                    "Centralized log collection and normalization",
                    "Real-time alerting based on correlation rules",
                    "Threat hunting dashboards and forensic visibility",
                    "Compliance-focused reporting",
                    "Integrated with external threat feeds"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start group">
                      <div className="mt-1 mr-4 flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 shadow-md shadow-blue-500/20 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 ring-1 ring-white/50">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-700 font-medium leading-relaxed group-hover:text-blue-900 transition-colors duration-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* 4. SOAR - Right Visual, Left Text */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1 relative"
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-teal-400/20 blur-3xl rounded-full"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                  <img src="/Logos/soar_image.png" alt="SOAR Dashboard" className="w-full h-auto object-contain" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1"
              >
                <h3 className="text-3xl font-bold text-slate-900 mb-2">SOAR</h3>
                <p className="text-lg text-slate-500 mb-6 font-medium">Security Orchestration, Automation & Response</p>
                <ul className="space-y-5">
                  {[
                    "Automated incident response workflows",
                    "Human-in-the-loop review for critical alerts",
                    "Ticketing system integration (e.g., Jira, ServiceNow)",
                    "Accelerated decision-making based on context",
                    "Reduced response time and fatigue"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start group">
                      <div className="mt-1 mr-4 flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 shadow-md shadow-blue-500/20 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 ring-1 ring-white/50">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-700 font-medium leading-relaxed group-hover:text-blue-900 transition-colors duration-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* 5. Threat Intelligence - Left Visual, Right Text */}
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1 relative"
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-purple-400/20 blur-3xl rounded-full"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                  <img src="/Logos/threatintelligence_image.png" alt="Threat Intelligence Dashboard" className="w-full h-auto object-contain" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1"
              >
                <h3 className="text-3xl font-bold text-slate-900 mb-6">Threat Intelligence & Threat Hunting</h3>
                <ul className="space-y-5">
                  {[
                    "MITRE ATT&CK-based threat mapping",
                    "Continuous threat hunting using behavior analytics",
                    "Custom threat feeds & IOC enrichment",
                    "Detection of APTs & stealthy attacks",
                    "Proactive defense beyond alert-based response"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start group">
                      <div className="mt-1 mr-4 flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 shadow-md shadow-blue-500/20 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 ring-1 ring-white/50">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-700 font-medium leading-relaxed group-hover:text-blue-900 transition-colors duration-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* 6. Vulnerability Management - Right Visual, Left Text */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1 relative"
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-orange-400/20 blur-3xl rounded-full"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                  <img src="/Logos/vulnerability_image.png" alt="Vulnerability Management Dashboard" className="w-full h-auto object-contain" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1"
              >
                <h3 className="text-3xl font-bold text-slate-900 mb-6">Vulnerability Management & Exploit Risk</h3>
                <ul className="space-y-5">
                  {[
                    "Continuous asset scanning & risk scoring (CVSS)",
                    "Integration with tools like Qualys/OpenVAS",
                    "Exploitability analysis with remediation plans",
                    "Executive summary reports",
                    "Patch orchestration workflow"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start group">
                      <div className="mt-1 mr-4 flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 shadow-md shadow-blue-500/20 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 ring-1 ring-white/50">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-700 font-medium leading-relaxed group-hover:text-blue-900 transition-colors duration-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* 7. Log Management - Left Visual, Right Text */}
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1 relative"
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-500/20 blur-3xl rounded-full"></div>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                  <img src="/Logos/log_image.png" alt="Log Management Dashboard" className="w-full h-auto object-contain" />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="flex-1"
              >
                <h3 className="text-3xl font-bold text-slate-900 mb-6">Log Management & Data Retention</h3>
                <ul className="space-y-5">
                  {[
                    "Efficient ingestion of logs from multiple data sources",
                    "Long-term log storage for compliance (GDPR, ISO 27001)",
                    "Secure searchable logs with access controls",
                    "Anomaly detection using historical baseline",
                    "Visual dashboards for log insights"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start group">
                      <div className="mt-1 mr-4 flex-shrink-0">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 shadow-md shadow-blue-500/20 flex items-center justify-center transform group-hover:scale-110 transition-all duration-300 ring-1 ring-white/50">
                          <Check size={12} className="text-white" strokeWidth={3} />
                        </div>
                      </div>
                      <span className="text-slate-700 font-medium leading-relaxed group-hover:text-blue-900 transition-colors duration-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

          </div>
        </div>
      </section>

      {/* Industries We Protect Section - Professional Dark Mode Carousel */}
      <section className="py-12 bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 relative overflow-hidden">
        {/* Enhanced Background Effects with Main Page Colors */}
        < div className="absolute inset-0 pointer-events-none" >
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-cyan-500/10 to-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-blue-500/10 to-cyan-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/3 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/2 to-transparent"></div>
        </div >

        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-20 items-center">

            {/* Left Side Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
              >
                Industries We
                <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mt-2">
                  Protect
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-xl text-gray-300 leading-relaxed max-w-lg"
              >
                Atlas Defenders provides specialized SOC-as-a-Service solutions across critical industries, ensuring compliance and protection tailored to each sector's unique requirements.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <Link
                  to="/industries"
                  className="inline-flex items-center bg-white text-blue-900 px-8 py-4 rounded font-bold text-lg shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl"
                >
                  <span>Explore Your Industry</span>
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Side Professional Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Professional Carousel Container */}
              <div className="relative">
                <div className="overflow-hidden rounded-2xl">
                  <div
                    className="flex transition-transform duration-1000 ease-in-out"
                    style={{ transform: `translateX(-${currentIndustrySlide * 35}%)` }}
                  >
                    {industriesCarousel.map((industry, index) => (
                      <div key={industry.id} className="w-1/2 flex-shrink-0 px-3">
                        <motion.div
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          viewport={{ once: true }}
                          className="group bg-white/95 backdrop-blur-xl rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-700 transform hover:scale-105 hover:-translate-y-3 border border-gray-200/20 h-full"
                          onMouseEnter={() => setIsCarouselPaused(true)}
                          onMouseLeave={() => setIsCarouselPaused(false)}
                        >
                          {/* Ultra Professional Image Container with Multi-Layer Blur */}
                          <div className="relative h-56 overflow-hidden">
                            <img
                              src={industry.image}
                              alt={industry.title}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            {/* Eye-Catching Gradient Overlay - No Blur */}
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/70 via-pink-500/60 to-orange-500/70 opacity-60 group-hover:opacity-80 transition-opacity duration-700"></div>
                            <div className="absolute inset-0 bg-gradient-to-t from-cyan-600/80 via-blue-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/50 via-transparent to-teal-500/50 opacity-70 group-hover:opacity-90 transition-opacity duration-700"></div>
                            <div className="absolute inset-0 bg-gradient-to-t from-rose-500/60 via-transparent to-violet-500/40 opacity-50 group-hover:opacity-70 transition-opacity duration-700"></div>

                            {/* Enhanced Industry Icon Overlay */}
                            <div className="absolute top-4 right-4 w-14 h-14 bg-white/95 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-700 transform translate-y-3 group-hover:translate-y-0 rotate-12 group-hover:rotate-0">
                              {industry.id === 'banking-finance' && <Landmark size={24} className="text-cyan-600" />}
                              {industry.id === 'healthcare' && <Heart size={24} className="text-cyan-600" />}
                              {industry.id === 'ecommerce-cloud' && <ShoppingCart size={24} className="text-cyan-600" />}
                              {industry.id === 'government-public' && <Building size={24} className="text-cyan-600" />}
                              {industry.id === 'critical-infrastructure' && <Server size={24} className="text-cyan-600" />}
                              {industry.id === 'telecom' && <Satellite size={24} className="text-cyan-600" />}
                            </div>


                          </div>

                          {/* Ultra Professional Content - Perfect Consistency */}
                          <div className="p-6 space-y-4 flex flex-col justify-between min-h-[220px]">
                            <div className="space-y-3">
                              <h3 className="text-xl font-bold text-gray-900 group-hover:text-cyan-700 transition-colors duration-300 line-clamp-2 leading-tight">
                                {industry.title}
                              </h3>
                              <p className="text-gray-600 text-sm leading-relaxed line-clamp-4">
                                {industry.description}
                              </p>
                            </div>

                            {/* Enhanced Professional Tags */}
                            <div className="flex flex-wrap gap-2 pt-3">
                              {industry.id === 'banking-finance' && (
                                <>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">PCI-DSS</span>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">Fraud Detection</span>
                                </>
                              )}
                              {industry.id === 'healthcare' && (
                                <>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">HIPAA</span>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">Patient Data</span>
                                </>
                              )}
                              {industry.id === 'ecommerce-cloud' && (
                                <>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">Payment Security</span>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">Cloud Security</span>
                                </>
                              )}
                              {industry.id === 'government-public' && (
                                <>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">FISMA</span>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">NIST</span>
                                </>
                              )}
                              {industry.id === 'critical-infrastructure' && (
                                <>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">SCADA</span>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">OT Security</span>
                                </>
                              )}
                              {industry.id === 'telecom' && (
                                <>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">Network Security</span>
                                  <span className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200">5G Security</span>
                                </>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Professional Progress Indicator */}
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {Array.from({ length: Math.max(1, industriesCarousel.length - 1) }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndustrySlide(index)}
                      className={`h-1 rounded-full transition-all duration-500 ${currentIndustrySlide === index
                        ? 'bg-cyan-400 w-8'
                        : 'bg-gray-600 hover:bg-gray-500 w-2'
                        }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Ultra Professional High-Tech Navigation - Below Divs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="flex items-center justify-center mt-20"
          >
            {/* High-Tech Navigation Container */}
            <div className="flex items-center space-x-8 px-8 py-4">

              {/* Previous Arrow */}
              <button
                onClick={prevSlide}
                className="group relative flex items-center justify-center w-12 h-12 transition-all duration-300 transform hover:scale-110 hover:-translate-x-1"
              >
                <ChevronLeft size={32} className="text-white group-hover:text-cyan-200 transition-all duration-300" />
              </button>

              {/* Professional Progress Dots */}
              <div className="flex items-center space-x-3">
                {Array.from({ length: Math.max(1, industriesCarousel.length - 1) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndustrySlide(index)}
                    className={`relative transition-all duration-500 ${currentIndustrySlide === index
                      ? 'w-8 h-2 bg-gradient-to-r from-white to-blue-200 rounded-full'
                      : 'w-2 h-2 bg-blue-300/50 hover:bg-white rounded-full hover:scale-125'
                      }`}
                  >
                    {currentIndustrySlide === index && (
                      <div className="absolute inset-0 bg-white rounded-full animate-pulse shadow-lg shadow-white/50"></div>
                    )}
                  </button>
                ))}
              </div>

              {/* Next Arrow */}
              <button
                onClick={nextSlide}
                className="group relative flex items-center justify-center w-12 h-12 transition-all duration-300 transform hover:scale-110 hover:translate-x-1"
              >
                <ChevronRight size={32} className="text-white group-hover:text-cyan-200 transition-all duration-300" />
              </button>


            </div>
          </motion.div>
        </div>
      </section >

      {/* Our Professional Certifications Section */}
      < section className="py-20 bg-white" >
        <div className="container max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Professional Certifications
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Atlas Defenders team holds industry-leading certifications to ensure the highest level of cybersecurity expertise and professional excellence
            </p>
          </motion.div>

          {/* Professional Certification Badges - 2 Row Layout */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* First Row - 5 Certifications */}
            <div className="flex justify-center items-center gap-8 lg:gap-12">
              {/* CompTIA Security+ */}
              <img
                src="/Certifications/Comptia Security+.png"
                alt="CompTIA Security+"
                className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg"
                onError={(e) => {
                  // Fallback for server deployment
                  const target = e.target as HTMLImageElement;
                  if (target.src.includes('Comptia Security+.png')) {
                    target.src = '/Certifications/Comptia%20Security%2B.png';
                  } else if (target.src.includes('Comptia%20Security%2B.png')) {
                    target.src = '/Certifications/Comptia_Security_Plus.png';
                  }
                }}
              />

              {/* CySA+ */}
              <img
                src="/Certifications/Comptia Cysa+.png"
                alt="CySA+"
                className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg"
                onError={(e) => {
                  // Fallback for server deployment
                  const target = e.target as HTMLImageElement;
                  if (target.src.includes('Comptia Cysa+.png')) {
                    target.src = '/Certifications/Comptia%20Cysa%2B.png';
                  } else if (target.src.includes('Comptia%20Cysa%2B.png')) {
                    target.src = '/Certifications/Comptia_CySA_Plus.png';
                  }
                }}
              />

              {/* CISSP */}
              <img src="/Certifications/CISSP.png" alt="CISSP" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

              {/* CISM */}
              <img src="/Certifications/Certified Information Security Manager.png" alt="CISM" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

              {/* CISA */}
              <img src="/Certifications/Certified Information Systems Auditor.png" alt="CISA" className="h-28 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />
            </div>

            {/* Second Row - 2 Certifications */}
            <div className="flex justify-center items-center gap-8 lg:gap-12">
              {/* OSDA */}
              <img src="/Certifications/OSDA.svg" alt="OSDA" className="h-36 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />

              {/* CDSA */}
              <img src="/Certifications/cdsa.svg" alt="CDSA" className="h-36 w-auto object-contain transition-all duration-300 hover:scale-110 hover:drop-shadow-lg" />
            </div>
          </motion.div>
        </div>
      </section >











      {/* CTA Section - Kaspersky Style Banner */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-3xl p-8 md:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10 shadow-2xl relative overflow-hidden"
          >
            {/* Background Texture/Effects */}
            <div className="absolute inset-0 opacity-30 pointer-events-none">
              <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
            </div>

            {/* Left Content */}
            <div className="relative z-10 max-w-3xl text-left">
              <span className="text-cyan-400 font-bold tracking-widest uppercase text-xs md:text-sm mb-4 block">
                ATLAS DEFENDERS SOC
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Is Your Business Protected 24/7?
              </h2>
              <p className="text-lg md:text-xl text-blue-100/90 leading-relaxed max-w-2xl">
                Don't wait for a breach to discover your blind spots. Let our SOC monitor, defend, and respond for you — 24/7.
              </p>
            </div>

            {/* Right Actions */}
            <div className="relative z-10 flex flex-col sm:flex-row gap-4 flex-shrink-0 w-full lg:w-auto">
              <Link
                to="/contact"
                className="inline-flex justify-center items-center bg-white text-blue-900 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors duration-300 shadow-lg"
              >
                Book a Free Consultation
              </Link>
              <Link
                to="/contact"
                className="inline-flex justify-center items-center bg-transparent border-2 border-white/30 text-white font-bold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors duration-300"
              >
                Let's Talk Security
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default SOCServicePage;